import React from 'react';
import { ChevronLeft, Check, LayoutGrid, Link as LinkIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTheme } from '../../hooks/useTheme';
import { MiniCalendar } from './MiniCalendar';
import { PeopleCalendarsSection } from './PeopleCalendarsSection';
import { usePermissions } from '../../lib/hooks/usePermissions';

// ============================================
// TYPES
// ============================================

export interface CalendarSource {
  id: string;
  name: string;
  type: 'crm' | 'google' | 'microsoft' | 'sales_activities' | 'project_tasks' | 'bookings' | 'alfadocs';
  color: string;
  enabled: boolean;
  googleCalendarId?: string;
  microsoftCalendarId?: string;
  isPrimaryMicrosoft?: boolean;
}

interface CalendarSourcesSidebarProps {
  calendarSources: CalendarSource[];
  onCalendarSourceToggle: (id: string) => void;
  onRefreshCalendar?: () => void;
  googleLoading?: boolean;
  googleError?: string;
  microsoftLoading?: boolean;
  onRefreshMicrosoftCalendar?: () => void;
  onOpenProfileModal?: () => void;
  canAccessDeals?: boolean;
  canViewBooking?: boolean;
  bookingStatusFilters?: string[];
  onBookingStatusFilterToggle?: (status: string) => void;
  alfadocsActive?: boolean;
  alfadocsStudios?: { practiceId: string; name: string }[];
  alfadocsStudioFilters?: string[] | null;
  onAlfadocsStudioFilterToggle?: (practiceId: string) => void;
  translations?: Record<string, string>;
  // Layout integration props
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
  onClose?: () => void;
  isMobile?: boolean;
  // Mini calendar props
  currentDate?: Date;
  onDateSelect?: (date: Date) => void;
  // Exit calendar mode
  onExitCalendarMode?: () => void;
}

// ============================================
// COLORED CHECKBOX COMPONENT
// ============================================

function ColoredCheckbox({
  checked,
  color,
  onChange
}: {
  checked: boolean;
  color: string;
  onChange: (e: React.MouseEvent) => void;
}) {
  return (
    <button
      onClick={onChange}
      className="w-4 h-4 rounded flex items-center justify-center transition-all flex-shrink-0"
    >
      {checked ? (
        <div
          style={{ backgroundColor: color }}
          className="w-full h-full rounded flex items-center justify-center"
        >
          <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} />
        </div>
      ) : (
        <div className="w-full h-full rounded border-2 border-gray-300 dark:border-gray-600" />
      )}
    </button>
  );
}

// ============================================
// SOURCE ITEM COMPONENT
// ============================================

function SourceItem({
  source,
  onToggle,
  showLoading,
  googleLoading
}: {
  source: CalendarSource;
  onToggle: (id: string) => void;
  showLoading?: boolean;
  googleLoading?: boolean;
}) {
  return (
    <div
      className="flex items-center gap-3 py-1.5 px-2 hover:bg-black/5 dark:hover:bg-white/5 rounded transition-colors cursor-pointer"
      onClick={() => onToggle(source.id)}
    >
      <ColoredCheckbox
        checked={source.enabled}
        color={source.color}
        onChange={(e) => {
          e.stopPropagation();
          onToggle(source.id);
        }}
      />
      <span className={`text-sm flex-1 ${source.enabled ? 'text-black/80 dark:text-white/80' : 'text-black/50 dark:text-white/50'}`}>
        {source.name}
      </span>
      {/* Loading indicator for individual calendar */}
      {showLoading && googleLoading && source.enabled && (
        <div className="flex items-center">
          <div className="flex space-x-0.5">
            <div className="w-1 h-1 bg-black/60 dark:bg-white/60 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
            <div className="w-1 h-1 bg-black/60 dark:bg-white/60 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
            <div className="w-1 h-1 bg-black/60 dark:bg-white/60 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================
// MAIN COMPONENT
// ============================================

export function CalendarSourcesSidebar({
  calendarSources,
  onCalendarSourceToggle,
  onRefreshCalendar,
  googleLoading = false,
  googleError,
  microsoftLoading = false,
  onRefreshMicrosoftCalendar,
  onOpenProfileModal,
  canAccessDeals = false,
  canViewBooking = true,
  bookingStatusFilters = ['confirmed', 'pending'],
  onBookingStatusFilterToggle,
  alfadocsActive = false,
  alfadocsStudios,
  alfadocsStudioFilters,
  onAlfadocsStudioFilterToggle,
  translations = {},
  isCollapsed = false,
  onToggleCollapse,
  onClose,
  isMobile = false,
  currentDate = new Date(),
  onDateSelect,
  onExitCalendarMode
}: CalendarSourcesSidebarProps) {
  const { isLight } = useTheme();

  // Check if error indicates no Google account connected
  const isNoGoogleConnected = googleError && (
    googleError.toLowerCase().includes('does not have a connected google') ||
    googleError.toLowerCase().includes('no google refresh token') ||
    googleError.toLowerCase().includes('user does not have a connected')
  );
  // "Collega Google" solo a chi puo' collegare il calendario (permesso calendar.sync)
  const { hasPermission } = usePermissions();
  const canSyncCalendar = hasPermission('calendar', 'sync');

  const t = {
    calendarSources: translations.calendarSources || 'Sorgenti Calendario',
    crmCalendar: translations.crmCalendar || 'Calendario CRM',
    googleCalendar: translations.googleCalendar || 'Google Calendar',
    microsoftCalendar: translations.microsoftCalendar || 'Microsoft Outlook',
    salesActivities: translations.salesActivities || 'Attività di Vendita',
    projectTasks: translations.projectTasks || 'Task Progetti',
    refresh: translations.refresh || 'Aggiorna',
    active: translations.active || 'attivi',
    mainMenu: translations.mainMenu || 'Menu Principale',
    connectGoogle: translations.connectGoogle || 'Collega Google',
    noGoogleConnected: translations.noGoogleConnected || 'Nessun account Google collegato',
    connectToSeeCalendars: translations.connectToSeeCalendars || 'Collega il tuo account Google per visualizzare i tuoi calendari'
  };

  // Collapsed state - show only icons/abbreviations
  if (isCollapsed && !isMobile) {
    return (
      <div className="h-full flex flex-col">
        {/* Header with logo and expand button */}
        <div className="h-12 flex items-center justify-center gap-1 px-1">
          <Link to="/dashboard" className="flex items-center justify-center" data-logo>
            <img
              src="/icona.png"
              alt="Logo Icon"
              className="h-7 w-7 object-contain rounded-lg"
            />
          </Link>
          {onToggleCollapse && (
            <button
              onClick={onToggleCollapse}
              className="flex items-center justify-center w-5 h-5 rounded hover:bg-black/10 transition-colors"
              style={{ color: '#000' }}
              title="Espandi menu"
            >
              <ChevronLeft className="h-3.5 w-3.5 rotate-180" />
            </button>
          )}
        </div>

        {/* Collapsed sources - just colored dots */}
        <div className="flex-1 overflow-y-auto px-2 py-4 space-y-2">
          {/* Menu Principale Icon */}
          {onExitCalendarMode && (
            <button
              onClick={onExitCalendarMode}
              className="w-full flex items-center justify-center py-1.5 rounded hover:bg-black/5 dark:hover:bg-white/5 transition-colors mb-2"
              title={t.mainMenu}
              style={{ color: 'var(--theme-menu-text)' }}
            >
              <LayoutGrid className="h-4 w-4" />
            </button>
          )}

          {/* Section label */}
          <div className="text-[9px] font-semibold uppercase tracking-wide text-center text-muted-foreground/70 mb-2">
            CAL
          </div>

          {calendarSources.map(source => (
            <button
              key={source.id}
              onClick={() => onCalendarSourceToggle(source.id)}
              className={`w-full flex items-center justify-center py-1.5 rounded transition-colors ${
                source.enabled ? 'bg-black/5 dark:bg-white/5' : 'hover:bg-black/5 dark:hover:bg-white/5'
              }`}
              title={source.name}
            >
              <span
                className={`w-3 h-3 rounded-full ${!source.enabled ? 'opacity-30' : ''}`}
                style={{ backgroundColor: source.color }}
              />
            </button>
          ))}
        </div>
      </div>
    );
  }

  // Full expanded state
  return (
    <div className="h-full flex flex-col">
      {/* Header with logo */}
      {!isMobile && (
        <div className="h-12 flex items-center justify-between px-3">
          <Link to="/dashboard" className="flex items-center" style={{ paddingLeft: '6px' }} data-logo>
            <img
              src={isLight ? "/logo_colorato.png" : "/logo_bianco.png"}
              alt="Logo"
              className="h-6 w-auto object-contain"
            />
          </Link>
          {onToggleCollapse && (
            <button
              onClick={onToggleCollapse}
              className="flex items-center justify-center w-7 h-7 rounded-md hover:bg-black/10 transition-colors"
              style={{ color: 'var(--theme-menu-text)' }}
              title="Collassa menu"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
          )}
        </div>
      )}

      {/* Da telefono l'intestazione (logo, profilo, chiudi) e' quella del menu principale in Layout: qui niente doppione */}

      {/* Calendar Sources Content — tutto scrollabile */}
      <div className="flex-1 overflow-y-auto min-h-0 px-4 pt-4 pb-4">
        {/* Menu Principale Button */}
        {onExitCalendarMode && (
          <button
            onClick={onExitCalendarMode}
            className="w-full flex items-center gap-3 py-2 px-3 mb-4 rounded-lg hover:bg-black/5 dark:hover:bg-white/5 transition-colors text-left"
            style={{ color: 'var(--theme-menu-text)' }}
          >
            <LayoutGrid className="h-4 w-4 flex-shrink-0" />
            <span className="text-sm font-medium">{t.mainMenu}</span>
          </button>
        )}

        {/* Mini Calendar */}
        {onDateSelect && (
          <div className="mb-6">
            <MiniCalendar
              currentDate={currentDate}
              onDateSelect={onDateSelect}
            />
          </div>
        )}

        {/* Calendar Sources List */}
        <div className="flex flex-col space-y-4">
          {/* Calendari delle persone: ogni cosa assegnata a qualcuno sta nel suo calendario */}
          <PeopleCalendarsSection />

          {/* AlfaDocs Section */}
          {alfadocsActive && calendarSources.some(source => source.type === 'alfadocs') && (
            <div className="mb-4 flex-shrink-0">
              <div className="text-xs font-semibold uppercase text-black/60 dark:text-white/60 mb-2">
                AlfaDocs
              </div>
              {calendarSources
                .filter(source => source.type === 'alfadocs')
                .map(source => (
                  <SourceItem
                    key={source.id}
                    source={source}
                    onToggle={onCalendarSourceToggle}
                  />
                ))}
              {/* Sub-filtri per studio (solo org multi-connessione) */}
              {(alfadocsStudios?.length ?? 0) >= 2
                && calendarSources.some(source => source.type === 'alfadocs' && source.enabled)
                && onAlfadocsStudioFilterToggle && (
                <div className="ml-7 mt-1 space-y-0.5">
                  {alfadocsStudios!.map(({ practiceId, name }) => {
                    const checked = !alfadocsStudioFilters || alfadocsStudioFilters.includes(practiceId);
                    return (
                      <div
                        key={practiceId}
                        className="flex items-center gap-2 py-1 px-2 hover:bg-black/5 dark:hover:bg-white/5 rounded cursor-pointer"
                        onClick={() => onAlfadocsStudioFilterToggle(practiceId)}
                      >
                        <ColoredCheckbox
                          checked={checked}
                          color="#2383E2"
                          onChange={(e) => { e.stopPropagation(); onAlfadocsStudioFilterToggle(practiceId); }}
                        />
                        <span className={`text-xs ${checked ? 'text-black/70 dark:text-white/70' : 'text-black/40 dark:text-white/40'}`}>
                          {name}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Google Calendar Section */}
          {(calendarSources.some(source => source.type === 'google') || googleLoading || isNoGoogleConnected)
            && !(isNoGoogleConnected && !canSyncCalendar) && (
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <div className="text-xs font-semibold uppercase text-black/60 dark:text-white/60">
                    {t.googleCalendar}
                  </div>
                  {googleLoading && (
                    <div className="flex items-center">
                      <div className="flex space-x-0.5">
                        <div className="w-1 h-1 bg-black/60 dark:bg-white/60 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                        <div className="w-1 h-1 bg-black/60 dark:bg-white/60 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                        <div className="w-1 h-1 bg-black/60 dark:bg-white/60 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                      </div>
                    </div>
                  )}
                </div>
                {onRefreshCalendar && !isNoGoogleConnected && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onRefreshCalendar();
                    }}
                    className="text-xs text-black/70 dark:text-white/70 hover:text-black/90 dark:hover:text-white/90 px-2 py-1 rounded hover:bg-black/5 dark:hover:bg-white/5 transition-colors"
                  >
                    {t.refresh}
                  </button>
                )}
              </div>
              <div>
                {/* Prompt per collegare Google Account */}
                {isNoGoogleConnected && onOpenProfileModal && (
                  <div className="p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
                    <p className="text-xs text-blue-800 dark:text-blue-200 mb-2">
                      {t.connectToSeeCalendars}
                    </p>
                    <button
                      onClick={onOpenProfileModal}
                      className="flex items-center gap-1.5 w-full justify-center px-3 py-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors"
                    >
                      <LinkIcon className="w-3 h-3" />
                      {t.connectGoogle}
                    </button>
                  </div>
                )}
                {/* Skeleton loader durante il caricamento iniziale */}
                {!isNoGoogleConnected && googleLoading && !calendarSources.some(source => source.type === 'google') && (
                  <div className="space-y-2 animate-pulse">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <div key={i} className="flex items-center gap-3 py-1.5 px-2">
                        <div className="w-4 h-4 rounded bg-gray-200 dark:bg-gray-700" />
                        <div className="flex-1 h-4 rounded bg-gray-200 dark:bg-gray-700" />
                      </div>
                    ))}
                  </div>
                )}
                {/* Lista calendari */}
                {!isNoGoogleConnected && calendarSources
                  .filter(source => source.type === 'google')
                  .map(source => (
                    <SourceItem
                      key={source.id}
                      source={source}
                      onToggle={onCalendarSourceToggle}
                      showLoading
                      googleLoading={googleLoading}
                    />
                  ))}
              </div>
            </div>
          )}

          {/* Microsoft Outlook Section (speculare a Google) */}
          {(calendarSources.some(source => source.type === 'microsoft') || microsoftLoading) && (
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <div className="text-xs font-semibold uppercase text-black/60 dark:text-white/60">
                    {t.microsoftCalendar}
                  </div>
                  {microsoftLoading && (
                    <div className="flex items-center">
                      <div className="flex space-x-0.5">
                        <div className="w-1 h-1 bg-black/60 dark:bg-white/60 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                        <div className="w-1 h-1 bg-black/60 dark:bg-white/60 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                        <div className="w-1 h-1 bg-black/60 dark:bg-white/60 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                      </div>
                    </div>
                  )}
                </div>
                {onRefreshMicrosoftCalendar && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onRefreshMicrosoftCalendar();
                    }}
                    className="text-xs text-black/70 dark:text-white/70 hover:text-black/90 dark:hover:text-white/90 px-2 py-1 rounded hover:bg-black/5 dark:hover:bg-white/5 transition-colors"
                  >
                    {t.refresh}
                  </button>
                )}
              </div>
              <div>
                {microsoftLoading && !calendarSources.some(source => source.type === 'microsoft') && (
                  <div className="space-y-2 animate-pulse">
                    {[1, 2, 3].map((i) => (
                      <div key={i} className="flex items-center gap-3 py-1.5 px-2">
                        <div className="w-4 h-4 rounded bg-gray-200 dark:bg-gray-700" />
                        <div className="flex-1 h-4 rounded bg-gray-200 dark:bg-gray-700" />
                      </div>
                    ))}
                  </div>
                )}
                {calendarSources
                  .filter(source => source.type === 'microsoft')
                  .map(source => (
                    <SourceItem
                      key={source.id}
                      source={source}
                      onToggle={onCalendarSourceToggle}
                      showLoading
                      googleLoading={microsoftLoading}
                    />
                  ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
